import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { CATEGORIES, BRAND } from "@/lib/data";
import logo from "@/lib/images/Untitled_design-removebg-preview.png";
import QuoteDialog from "@/components/QuoteDialog";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Export", path: "/export" },
  { name: "Contact", path: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.10)] border-b border-[#6B3E26]/10"
          : "bg-black/20 backdrop-blur-sm border-b border-white/10"
      }`}
    >
      {/* Announcement bar - desktop only, hidden when scrolled */}
      {!scrolled && (
        <div className="hidden md:block bg-white/10 backdrop-blur-sm text-white/80 text-xs tracking-[0.18em] uppercase border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-2 flex items-center justify-between">
            <span>Bulk Wholesale & Export Specialist</span>
            <span>Pan-India Delivery · International Shipping</span>
            <a
              href={`tel:${BRAND.phone}`}
              className="hover:text-[#E5A52A] transition-colors"
              data-testid="header-phone-link"
            >
              {BRAND.phone}
            </a>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-12 py-2 md:py-3 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2 group">
          <img
            src={logo}
            alt="Desi Masala Hub"
            className="h-12 md:h-20 w-auto object-contain group-hover:scale-105 transition-transform drop-shadow-md"
          />
        </Link>

        {/* Mobile: centered brand strip */}
        <div className="flex-1 flex justify-center lg:hidden">
          <span className={`text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${
            scrolled ? "text-[#6B3E26]" : "text-white/80"
          }`}>Wholesale · Export</span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          <NavLink
            to="/"
            data-testid="nav-home"
            className={({ isActive }) =>
              `px-4 py-2 rounded-sm text-sm font-medium tracking-wide transition-colors ${
                isActive
                  ? scrolled ? "text-[#C38B2A]" : "text-[#E5A52A]"
                  : scrolled ? "text-[#1A1A1A] hover:text-[#C38B2A] hover:bg-[#6B3E26]/5" : "text-white/90 hover:text-white hover:bg-white/10"
              }`
            }
          >
            Home
          </NavLink>

          {/* Mega menu trigger */}
          <div
            className="relative"
            onMouseEnter={() => setMenuOpen(true)}
            onMouseLeave={() => setMenuOpen(false)}
          >
            <button
              data-testid="mega-menu-trigger"
              className={`flex items-center gap-1 px-4 py-2 rounded-sm text-sm font-medium tracking-wide transition-colors ${
                scrolled ? "text-[#1A1A1A] hover:text-[#C38B2A] hover:bg-[#6B3E26]/5" : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              Products
              <ChevronDown className={`w-4 h-4 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>
            {menuOpen && (
              <div
                data-testid="mega-menu-panel"
                className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(880px,90vw)]"
              >
                <div className="bg-white border border-[#6B3E26]/10 rounded-sm shadow-2xl p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                  {CATEGORIES.map((c) => (
                    <Link
                      to={`/${c.slug}`}
                      key={c.slug}
                      data-testid={`mega-link-${c.slug}`}
                      className="group"
                    >
                      <div className="aspect-[4/3] overflow-hidden rounded-sm mb-2 bg-[#F7F4ED]">
                        <img
                          src={c.image}
                          alt={c.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          loading="lazy"
                        />
                      </div>
                      <div className="font-serif-display text-lg text-[#6B3E26] group-hover:text-[#C38B2A] transition-colors">
                        {c.name}
                      </div>
                      <div className="text-xs text-[#4A4A4A] mt-0.5">
                        {c.products.length} items
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {NAV_LINKS.filter((l) => l.name !== "Home").map((l) => (
            <NavLink
              key={l.name}
              to={l.path}
              data-testid={`nav-${l.name.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-sm text-sm font-medium tracking-wide transition-colors ${
                  isActive
                    ? scrolled ? "text-[#C38B2A]" : "text-[#E5A52A]"
                    : scrolled ? "text-[#1A1A1A] hover:text-[#C38B2A] hover:bg-[#6B3E26]/5" : "text-white/90 hover:text-white hover:bg-white/10"
                }`
              }
            >
              {l.name}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <QuoteDialog
            trigger={
              <Button
                data-testid="header-quote-button"
                className={`rounded-sm px-6 py-5 text-sm tracking-wide transition-all ${
                  scrolled
                    ? "bg-[#C38B2A] hover:bg-[#A37423] text-white"
                    : "bg-white/15 hover:bg-white/25 text-white border border-white/40 backdrop-blur-sm"
                }`}
              >
                Request Quote
              </Button>
            }
          />
        </div>

        {/* Mobile menu button */}
        <button
          data-testid="mobile-menu-button"
          onClick={() => setMobileOpen((v) => !v)}
          className={`lg:hidden p-2 transition-colors ${
            scrolled ? "text-[#6B3E26]" : "text-white"
          }`}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
    </header>

      {/* Mobile nav - dark glass drawer - outside header to avoid z-index clipping */}
      <div
        data-testid="mobile-nav"
        className={`lg:hidden fixed inset-0 z-[60] transition-all duration-300 ease-in-out ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />

        {/* Drawer panel */}
        <div className={`absolute top-0 right-0 h-full w-4/5 max-w-xs bg-[#1a0f0a]/95 backdrop-blur-xl flex flex-col transition-transform duration-300 ease-in-out border-l border-white/10 ${
          mobileOpen ? "translate-x-0" : "translate-x-full"
        }`}>
          {/* Drawer header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
            <Link to="/" onClick={() => setMobileOpen(false)}>
              <img src={logo} alt="Desi Masala Hub" className="h-12 w-auto object-contain drop-shadow-md" />
            </Link>
            <button onClick={() => setMobileOpen(false)} className="p-2 text-white/70 hover:text-white" aria-label="Close menu">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Nav links */}
          <div className="flex-1 overflow-y-auto px-5 py-3 flex flex-col">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.name}
                to={l.path}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${l.name.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 border-b border-white/10 text-sm font-medium tracking-wide transition-colors ${
                    isActive ? "text-[#E5A52A]" : "text-white/80 hover:text-white"
                  }`
                }
              >
                {l.name}
              </NavLink>
            ))}

            {/* Categories accordion */}
            <details className="group">
              <summary className="py-3 border-b border-white/10 text-sm font-medium text-white/80 flex items-center justify-between cursor-pointer list-none hover:text-white">
                Products
                <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180 text-white/50" />
              </summary>
              <div className="pl-3 flex flex-col mt-1">
                {CATEGORIES.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/${c.slug}`}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-cat-${c.slug}`}
                    className="py-2.5 border-b border-white/5 text-xs text-[#E5A52A]/80 hover:text-[#E5A52A] tracking-wide transition-colors"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </details>
          </div>

          {/* CTA */}
          <div className="px-5 py-5 border-t border-white/10 space-y-3">
            <QuoteDialog
              trigger={
                <Button
                  data-testid="mobile-quote-button"
                  className="bg-[#C38B2A] hover:bg-[#A37423] text-white w-full rounded-sm py-4 text-sm tracking-wide"
                >
                  Request Quote
                </Button>
              }
            />
            <a
              href={`tel:${BRAND.phone}`}
              className="flex items-center justify-center gap-2 text-xs text-white/60 hover:text-white/90 transition-colors"
            >
              {BRAND.phone}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
